import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Heart, MessageCircle, Users, User, Menu, X, LogOut, Settings, Bell, Home, Video, Music, Radio, BookOpen, Crown, Rocket, Gift, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "@/App";
import { useWebSocket } from "@/contexts/WebSocketContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isConnected, notifications, onlineFriends } = useWebSocket();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchUnreadCount();
  }, [notifications]);

  const fetchUnreadCount = async () => {
    try {
      const response = await fetch(`${API}/notifications/unread-count`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count);
      }
    } catch (error) {
      toast.error("Failed to load data");
    }
  };

  const navItems = [
    { icon: Home, label: "Home", path: "/dashboard" },
    { icon: Video, label: "Videos", path: "/videos" },
    { icon: Music, label: "Music", path: "/music" },
    { icon: Radio, label: "Live", path: "/live" },
    { icon: BookOpen, label: "Stories", path: "/stories" },
    { icon: Heart, label: "Browse", path: "/discover" },
    { icon: Sparkles, label: "AI Date", path: "/ai-dating" },
    { icon: MessageCircle, label: "Mail", path: "/matches" },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="app-header">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-1" data-testid="header-logo">
            <span className="text-xl font-bold glitter-text">Our</span>
            <span className="text-xl font-bold text-[#00FFFF]" style={{ textShadow: '0 0 10px #00FFFF' }}>Space</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`nav-link flex items-center gap-1 text-sm ${
                  location.pathname === item.path ? "active" : ""
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </button>
            ))}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-2">
            {/* Online indicator */}
            {onlineFriends.length > 0 && (
              <div className="hidden md:flex items-center gap-1 px-2 py-1 border border-[#00FF00] text-xs">
                <span className="online-dot w-2 h-2"></span>
                <span className="text-[#00FF00]">{onlineFriends.length} online</span>
              </div>
            )}

            {/* Connection Status */}
            <div
              className={`hidden md:block w-2 h-2 rounded-full ${
                isConnected ? "bg-[#00FF00] shadow-[0_0_5px_#00FF00]" : "bg-[#FF0000]"
              }`}
              title={isConnected ? "Connected" : "Offline"}
            />

            {/* Notifications */}
            <button
              onClick={() => navigate("/notifications")}
              className="relative myspace-btn px-2 py-1"
              data-testid="notifications-btn"
            >
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 new-indicator px-1">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>

            {/* User Menu (Desktop) */}
            <div className="hidden md:block">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 myspace-btn px-2 py-1"
                    data-testid="user-menu-btn"
                  >
                    {user?.picture ? (
                      <img
                        src={user.picture}
                        alt={user.name}
                        className="w-6 h-6 border border-[#FF00FF] object-cover"
                      />
                    ) : (
                      <User className="w-4 h-4" />
                    )}
                    <span className="text-sm">{user?.name?.split(" ")[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-48 border-2 border-[#FF00FF] bg-[#000033]"
                  style={{ boxShadow: '0 0 20px rgba(255,0,255,0.5)' }}
                >
                  <DropdownMenuItem
                    onClick={() => navigate(`/profile/${user?.user_id}`)}
                    className="cursor-pointer text-[#FF66FF] hover:text-[#00FFFF] hover:bg-[rgba(255,0,255,0.2)]"
                    data-testid="menu-profile"
                  >
                    <User className="w-4 h-4 mr-2" />
                    My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate("/edit-profile")}
                    className="cursor-pointer text-[#FF66FF] hover:text-[#00FFFF] hover:bg-[rgba(255,0,255,0.2)]"
                    data-testid="menu-settings"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Edit Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[#6600CC]" />
                  <DropdownMenuItem
                    onClick={() => navigate("/premium")}
                    className="cursor-pointer text-[#FFD700] hover:bg-[rgba(255,215,0,0.2)]"
                    data-testid="menu-premium"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    {user?.is_premium_active ? "Gold Active" : "Get Gold"}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate("/boost")}
                    className="cursor-pointer text-[#FF00FF] hover:bg-[rgba(255,0,255,0.2)]"
                    data-testid="menu-boost"
                  >
                    <Rocket className="w-4 h-4 mr-2" />
                    {user?.boost_active ? "Boost Active" : "Boost Profile"}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate("/gifts")}
                    className="cursor-pointer text-[#FFD700] hover:bg-[rgba(255,215,0,0.2)]"
                    data-testid="menu-gifts"
                  >
                    <Gift className="w-4 h-4 mr-2" />
                    Coins & Gifts
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate("/plans")}
                    className="cursor-pointer text-[#FF00FF] hover:bg-[rgba(255,0,255,0.2)]"
                    data-testid="menu-plans"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Plans & Subscriptions
                  </DropdownMenuItem>
                  {user?.active_plans?.includes("artist_pro") && (
                    <DropdownMenuItem
                      onClick={() => navigate("/distribution")}
                      className="cursor-pointer text-[#00FFFF] hover:bg-[rgba(0,255,255,0.2)]"
                      data-testid="menu-distribution"
                    >
                      <Music className="w-4 h-4 mr-2" />
                      Distribution Hub
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-[#6600CC]" />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="cursor-pointer text-[#FF0000] hover:bg-[rgba(255,0,0,0.2)]"
                    data-testid="menu-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Log Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden myspace-btn px-2 py-1"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t-2 border-[#6600CC] bg-[rgba(0,0,51,0.95)]"
          >
            <div className="container mx-auto px-4 py-4 space-y-2">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full nav-link flex items-center gap-2 ${
                    location.pathname === item.path ? "active" : ""
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </button>
              ))}
              <hr className="border-[#6600CC]" />
              <button
                onClick={() => {
                  navigate("/premium");
                  setMobileMenuOpen(false);
                }}
                className="w-full nav-link flex items-center gap-2 text-[#FFD700]"
                data-testid="mobile-menu-premium"
              >
                <Crown className="w-4 h-4" />
                {user?.is_premium_active ? "Gold Active" : "Get Gold"}
              </button>
              <button
                onClick={() => {
                  navigate("/boost");
                  setMobileMenuOpen(false);
                }}
                className="w-full nav-link flex items-center gap-2 text-[#FF00FF]"
                data-testid="mobile-menu-boost"
              >
                <Rocket className="w-4 h-4" />
                {user?.boost_active ? "Boost Active" : "Boost Profile"}
              </button>
              <button
                onClick={() => {
                  navigate("/gifts");
                  setMobileMenuOpen(false);
                }}
                className="w-full nav-link flex items-center gap-2 text-[#FFD700]"
                data-testid="mobile-menu-gifts"
              >
                <Gift className="w-4 h-4" />
                Coins & Gifts
              </button>
              <button
                onClick={() => {
                  navigate("/plans");
                  setMobileMenuOpen(false);
                }}
                className="w-full nav-link flex items-center gap-2 text-[#FF00FF]"
                data-testid="mobile-menu-plans"
              >
                <Crown className="w-4 h-4" />
                Plans & Subscriptions
              </button>
              {user?.active_plans?.includes("artist_pro") && (
                <button
                  onClick={() => {
                    navigate("/distribution");
                    setMobileMenuOpen(false);
                  }}
                  className="w-full nav-link flex items-center gap-2 text-[#00FFFF]"
                  data-testid="mobile-menu-distribution"
                >
                  <Music className="w-4 h-4" />
                  Distribution Hub
                </button>
              )}
              <button
                onClick={() => {
                  navigate(`/profile/${user?.user_id}`);
                  setMobileMenuOpen(false);
                }}
                className="w-full nav-link flex items-center gap-2"
              >
                <User className="w-4 h-4" />
                My Profile
              </button>
              <button
                onClick={handleLogout}
                className="w-full nav-link flex items-center gap-2 text-[#FF0000]"
              >
                <LogOut className="w-4 h-4" />
                Log Out
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
